import React from 'react';
import PropTypes from 'prop-types';
import { Dropdown } from 'primereact/dropdown';
import { Menu } from 'primereact/menu';
import GanttChart from '../../Common/GanttChart/GanttChart';
import Toggle from '../../Common/Toggle/Toggle';
import { EVENT_DATE_FORMAT, GANTT_ZOOM_LEVELS } from '../../../constants/constants';
import ChangeStartDateModal from '../Calendar/ChangeStartDateModal';
import ChangeTargetDateModal from '../Calendar/ChangeTargetDateModal';
import Notification from '../../Common/Notification';
import LoadingIndicator from '../../Common/LoadingIndicator/LoadingIndicator';
import Moment from 'moment';
import './GanttChartView2.scss';
import { MOBILE_WINDOW_BREAKPOINT } from '../../../constants/constants';

const MENU_OPTIONS = {
	ExportPdf: 'Export PDF',
	ExportPng: 'Export PNG',
};

export class GanttChartView2 extends React.Component {
	constructor(props, context) {
		super(props, context);

		// lastSearchTime is passed via props which triggers the conversion of props.locationTasks to chartData
		// lastSearchTime in this state is separate and used to trigger the updating of the gantt chart with the newly converted tasks
		// They are daisy chained because once this component sees the locationTasks changed, we need to convert them before telling the gannt chart the chartData has changed
		this.state = {
			lastSearchTime: new Date(), // used to trigger load, reload, undo off gantt chart
			isLoading: false,
			showChangeStartDateModal: false,
			showChangeTargetDateModal: false,
			showCriticalPath: false,
			lastClickedEvent: null,
			dateChangeDeltaDays: 0,
			chartData: {
				data: [],
				links: [],
				markers: [],
			},
			zoomLevel: GANTT_ZOOM_LEVELS.Month.value,
			ganttExportToPDF: false,
			ganttExportToPNG: false,
		};
	}

	componentDidMount = async () => {
		this.convertTasksToGanttData(this.props.locationTasks);
	};

	componentDidUpdate(prevProps) {
		if (this.props.lastSearchTime && this.props.lastSearchTime !== prevProps.lastSearchTime) {
			const locationTasks = [...this.props.locationTasks];
			locationTasks.sort((a, b) => new Date(a.targetDate) - new Date(b.targetDate));

			this.convertTasksToGanttData(locationTasks);
		}
	}

	convertTasksToGanttData = (taskObjects) => {
		const retval = {
			data: [],
			links: [],
		};

		// if there are multiple locations selected, make sure projects and tasks are properly nested
		if (this.props.locations.length > 1) {
			this.props.locations.forEach((location) => {
				const locationEvent = {
					id: 'Location_' + location.id,
					text: location.name,
					end_date: new Date(2019, 9, 10),
					open: true,
				};
				retval.data.push(locationEvent);
			});
		}

		let projectsFound = [];

		taskObjects.forEach((locationTask) => {
			// Tasks are sorted by LocationId, ProjectName, targetDate
			// if this belongs to a project that we haven't encountered yet, add the project to the array of found projects and create a new event for it so we can nest the locationTasks within it
			// must give the projects a unique id so they don't conflict with taskIds
			let project = null;
			if (locationTask.projectId) {
				project = projectsFound.find((m) => m.id === 'Project_' + locationTask.projectId);
				if (!project) {
					// make the start of the project the start or target of the first sorted locationTask
					const firstTaskStartDate = locationTask.startDate ? locationTask.startDate : locationTask.targetDate;
					const projectStartDate = Moment.utc(firstTaskStartDate);
					project = {
						id: 'Project_' + locationTask.projectId,
						text: locationTask.projectName,
						start_date: projectStartDate.format(EVENT_DATE_FORMAT),
						open: true,
						parent: this.props.locations.length > 1 ? 'Location_' + locationTask.locationKey : null,
					};
					projectsFound.push(project);
					retval.data.push(project);
				}
			}

			let locationEventParent = null;
			if (project) {
				locationEventParent = project.id;
			} else if (this.props.locations.length > 1) {
				locationEventParent = 'Location_' + locationTask.locationKey;
			}

			let locationEventText = locationTask.name;
			if (locationTask.isMilestone) {
				locationEventText = '&#x25C6; ' + locationTask.name;
			}

			let eventTask = {
				id: locationTask.id,
				text: locationEventText,
				eh_task: locationTask,
				eh_showStatus: true,
				parent: locationEventParent,
			};
			// if the task has no start date, make the event.start the task.targetDate
			if (!locationTask.startDate) {
				eventTask.eh_noStartDate = true;
				eventTask.duration = 1;
				eventTask.start_date = Moment.utc(locationTask.targetDate).format(EVENT_DATE_FORMAT);
			} else {
				eventTask.eh_noStartDate = false;
				const startDate = Moment.utc(locationTask.startDate);
				const targetDate = Moment.utc(locationTask.targetDate);

				// add 1 to duration so the target date is included visually
				eventTask.duration = Moment.duration(targetDate.diff(startDate)).as('days') + 1;
				eventTask.start_date = startDate.format(EVENT_DATE_FORMAT);
			}

			// see if this task has any contingents and add a link
			const parents = taskObjects.filter((m) => m.contingentId === locationTask.id);
			if (parents) {
				parents.forEach(function (parent) {
					retval.links.push({
						id: parent.id,
						source: parent.id,
						target: locationTask.id,
						type: 0,
					});
				});
			}

			retval.data.push(eventTask);
		});

		// setting the lastSearchTime here will reload the gantt chart.
		this.setState({ chartData: retval, lastSearchTime: new Date() });
	};

	onSelectTask = (taskId) => {
		const task = this.props.locationTasks.find((m) => m.id === taskId);
		this.props.onSelectTask(task);
	};

	isMobile = () => {
		return this.props.windowWidth < MOBILE_WINDOW_BREAKPOINT;
	};

	onMoveTask = (taskId, deltaDays) => {
		const task = this.props.locationTasks.find((m) => m.id === taskId);

		// don't allow prereq task to go over it's parents targetDate
		if (task.contingentId) {
			const parentTask = this.props.locationTasks.find((m) => m.id === task.contingentId);

			if (!parentTask) {
				Notification('error', '', 'Parent task not found.');
				this.undoChanges();
			}

			let taskEndDate = Moment(task.targetDate);
			taskEndDate = taskEndDate.add(deltaDays, 'days');
			const parentTargetDate = Moment(parentTask.targetDate);

			const diffDays = Moment.duration(taskEndDate.diff(parentTargetDate)).as('days');

			if (diffDays >= 1) {
				Notification('error', '', 'A contingent task cannot start or end before the start of its parent');
				this.undoChanges();
				return;
			}
		}

		if (!task.startDate && this.props.settings.allowLocationTaskDateChanges) {
			// changes only the target date
			this.setState({
				lastClickedEvent: task,
				showChangeTargetDateModal: true,
				dateChangeDeltaDays: deltaDays,
			});
		} else {
			// allows for changing both start and target dates, but contains logic to only allow target date change if user has access
			this.setState({
				lastClickedEvent: task,
				showChangeStartDateModal: true,
				dateChangeDeltaDays: deltaDays,
			});
		}
	};

	onResizeTask = (taskId, originalStart, originalTarget, updatedStart, updatedTarget) => {
		const task = this.props.locationTasks.find((m) => m.id === taskId);

		// don't allow prereq task to go over it's parents targetDate
		if (task.contingentId) {
			const parentTask = this.props.locationTasks.find((m) => m.id === task.contingentId);

			if (!parentTask) {
				Notification('error', '', 'Parent task not found.');
				this.undoChanges();
			}

			const parentTargetDate = Moment(parentTask.targetDate);
			const diffDays = Moment.duration(updatedTarget.diff(parentTargetDate)).as('days');

			if (diffDays >= 1) {
				Notification('error', '', 'A contingent task cannot start or end before the start of its parent');
				this.undoChanges();
				return;
			}
		}

		// figure out which end was resized - only one side can change at a time
		// != does not work so check duration diff
		if (Moment.duration(updatedStart.diff(originalStart)).as('days') !== 0) {
			// anyone can change the start date
			this.props.updateTaskDates(taskId, updatedStart, originalTarget);
		} else if (Moment.duration(updatedTarget.diff(originalTarget)).as('days')) {
			// if target date was resized, make sure they have access
			if (this.props.settings.allowLocationTaskDateChanges === false) {
				Notification('error', 'Error', 'You do not have access to change target dates');
				this.undoChanges();
			} else {
				this.props.updateTaskDates(taskId, originalStart, updatedTarget);
			}
		}
	};

	onCloseChangeStartDateModal = () => {
		this.undoChanges();
		this.setState({ showChangeStartDateModal: false });
	};

	onCompleteChangeTargetDateModal = () => {
		this.setState({ showChangeTargetDateModal: false }, () => {
			const locationTask = this.state.lastClickedEvent;
			const newTargetDate = Moment(locationTask.targetDate).add(this.state.dateChangeDeltaDays, 'days');
			this.props.updateTaskDates(locationTask.id, null, newTargetDate);
		});
	};

	onCloseChangeTargetDateModal = () => {
		this.undoChanges();
		this.setState({ showChangeTargetDateModal: false });
	};

	onCompleteChangeStartDateModal = (startDate, targetDate) => {
		this.setState({ showChangeStartDateModal: false }, () => {
			const locationTask = this.state.lastClickedEvent;
			this.props.updateTaskDates(locationTask.id, startDate, targetDate);
		});
	};

	onRemoveLink = (parentId) => {
		this.props.updateContingent(parentId, null);
	};

	onAddLink = (parentId, childId) => {
		this.props.updateContingent(parentId, childId);
	};

	undoChanges = () => {
		// resets the gantt chart without having to hit the api
		this.convertTasksToGanttData(this.props.locationTasks);
	};

	getHeight = () => {
		// makes the chart fill the rest of the page with enough space below it to match the margins on the sides
		return window.innerHeight - 175;
	};

	onZoomChange = (out) => {
		let newLevel = '';

		if (out && this.state.zoomLevel > GANTT_ZOOM_LEVELS.Month.value) {
			newLevel = this.state.zoomLevel - 1;
		} else if (!out && this.state.zoomLevel < GANTT_ZOOM_LEVELS.Day.value) {
			newLevel = this.state.zoomLevel + 1;
		}

		if (newLevel !== '') {
			this.setState({ zoomLevel: newLevel });
		}
	};

	onDownloadClick = (e) => {
		this.menu.toggle(e);
	};

	menuOptionClick = (e) => {
		switch (e.item.value) {
			case MENU_OPTIONS.ExportPdf: {
				this.setState({ ganttExportToPDF: !this.state.ganttExportToPDF });
				break;
			}
			case MENU_OPTIONS.ExportPng: {
				this.setState({ ganttExportToPNG: !this.state.ganttExportToNG });
				break;
			}
			default: {
				// Do nothing
				break;
			}
		}
	};

	render() {
		const isMobile = this.isMobile();

		const zoomOptions = Object.keys(GANTT_ZOOM_LEVELS).map((key) => ({ label: key, value: GANTT_ZOOM_LEVELS[key].value }));

		const menuOptions = [
			{ label: MENU_OPTIONS.ExportPdf, value: MENU_OPTIONS.ExportPdf, command: this.menuOptionClick },
			{ label: MENU_OPTIONS.ExportPng, value: MENU_OPTIONS.ExportPng, command: this.menuOptionClick },
		];

		return (
			<div className={`gantt-chart-view ${isMobile ? 'mobile' : ''}`}>
				<Menu model={menuOptions} popup={true} ref={(el) => (this.menu = el)} />
				<LoadingIndicator isLoading={this.props.isFetchingTasks}>
					<div className='gantt-header-container'>
						<div className='row'>
							<div className='col-md-6'>
								<Dropdown
									className='ui-column-filter'
									value={this.state.zoomLevel}
									options={zoomOptions}
									onChange={(e) => this.setState({ zoomLevel: e.value })}
									style={{
										width: '250px',
									}}
								/>
								<button
									type='button'
									className='btn pacer61-secondary-btn ms-2 me-2'
									disabled={this.state.zoomLevel === GANTT_ZOOM_LEVELS.Day.value}
									onClick={() => this.onZoomChange(false)}>
									<i className='fa-regular fa-magnifying-glass-plus fa-xl'></i>
								</button>
								<button
									type='button'
									className='btn pacer61-secondary-btn me-2'
									disabled={this.state.zoomLevel === GANTT_ZOOM_LEVELS.Month.value}
									onClick={() => this.onZoomChange(true)}>
									<i className='fa-regular fa-magnifying-glass-minus fa-xl'></i>
								</button>
							</div>
							<div className='col-md-6'>
								<Toggle
									name='showCriticalPath'
									text='Critical Path'
									checked={this.state.showCriticalPath}
									onChange={(name, checked) => this.setState({ showCriticalPath: checked })}
								/>
								<button
									type='button'
									className='btn pacer61-secondary-btn me-2'
									onClick={(e) => this.onDownloadClick(e)}
									alt='Export'
									title='Export'>
									<i className='fa-regular fa-cloud-arrow-down' style={{ fontSize: '15px' }}></i>
								</button>
							</div>
						</div>
					</div>

					<GanttChart
						chartData={this.state.chartData}
						height={this.getHeight()}
						onSelectTask={this.onSelectTask}
						onMoveTask={this.onMoveTask}
						onResizeTask={this.onResizeTask}
						lastSearchTime={this.state.lastSearchTime}
						zoomLevel={this.state.zoomLevel}
						exportToPDF={this.state.ganttExportToPDF}
						exportToPNG={this.state.ganttExportToPNG}
						startOnMonday={this.props.settings.calendarStartDay != 0}
						isTaskDependenciesActive={this.props.settings.isTaskDependenciesActive}
						isStartDateActive={this.props.settings.isStartDateActive}
						showCriticalPath={this.state.showCriticalPath}
						ganttColumns={[
							{
								name: 'text',
								label: 'Task Name',
								tree: true,
								align: 'left',
								width: isMobile ? 50 : 350,
							},
						]}
					/>
				</LoadingIndicator>

				{this.state.showChangeStartDateModal && (
					<ChangeStartDateModal
						task={this.state.lastClickedEvent}
						onCancel={this.onCloseChangeStartDateModal}
						onComplete={this.onCompleteChangeStartDateModal}
						deltaDays={this.state.dateChangeDeltaDays}
						userCanChangeTargetDate={this.props.settings.allowLocationTaskDateChanges}
					/>
				)}

				{this.state.showChangeTargetDateModal && (
					<ChangeTargetDateModal
						task={this.state.lastClickedEvent}
						onCancel={this.onCloseChangeTargetDateModal}
						onComplete={this.onCompleteChangeTargetDateModal}
						deltaDays={this.state.dateChangeDeltaDays}
					/>
				)}
			</div>
		);
	}
}

GanttChartView2.propTypes = {
	locationTasks: PropTypes.array.isRequired,
	onSelectTask: PropTypes.func.isRequired,
	isFetchingTasks: PropTypes.bool.isRequired,
	columnFilters: PropTypes.object.isRequired,
	settings: PropTypes.object.isRequired,
	locations: PropTypes.array.isRequired,
	updateTaskDates: PropTypes.func.isRequired,
	updateContingent: PropTypes.func.isRequired,
	lastSearchTime: PropTypes.instanceOf(Date),
	windowWidth: PropTypes.number,
};

export default GanttChartView2;
